import api from './axios';
import type { User, InsuranceCompany } from '../types/user.types';

export interface ProfileResponse {
  user: User;
  company: InsuranceCompany;
}

export type LoginApiResponse =
  | { status: 'success'; message: string; data: { user: User; must_change_password: boolean; authorization: { token: string; expiresIn: number } } }
  | { status: 'mfa_required'; message: string; token: string };

export async function login(id: string, password: string): Promise<LoginApiResponse> {
  const { data } = await api.post<LoginApiResponse>('/api/v1/auth/login', { id, password });
  return data;
}

export async function verifyMfa(code: string): Promise<string> {
  const { data } = await api.post<{ data: { token: string } }>(
    '/api/v2/rbac/mfa/verify',
    { otp: code },
  );
  return data.data.token;
}

export async function resendMfaOtp(): Promise<void> {
  await api.post('/api/v2/rbac/mfa/resend', {});
}

export async function getProfile(): Promise<ProfileResponse> {
  const { data } = await api.get<{ data: ProfileResponse }>('/api/v2/auth/profile');
  return data.data;
}

export async function changePassword(new_password: string): Promise<{ token: string; expiresIn: number }> {
  const { data } = await api.post<{ data: { token: string; expiresIn: number } }>(
    '/api/v2/auth/change-password',
    { new_password },
  );
  return data.data;
}

export async function updateProfile(formData: FormData): Promise<User> {
  const { data } = await api.patch<{ data: User }>('/api/v2/auth/profile', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.data;
}

export async function forgotPasswordRequest(email: string): Promise<{ status: string; message: string }> {
  const { data } = await api.post<{ status: string; message: string }>(
    '/api/v2/auth/forgot-password',
    { email },
  );
  return data;
}

export async function forgotPasswordVerify(email: string, token: string): Promise<{ reset_token: string }> {
  const { data } = await api.post<{ data: { reset_token: string } }>(
    '/api/v2/auth/forgot-password/verify',
    { email, token },
  );
  return data.data;
}

export async function forgotPasswordReset(
  email: string,
  new_password: string,
  reset_token: string,
): Promise<{ status: string; message: string }> {
  const { data } = await api.post<{ status: string; message: string }>(
    '/api/v2/auth/forgot-password/reset',
    { email, new_password, reset_token },
  );
  return data;
}
